import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Circle, Loader2, PackageCheck, Truck, Sparkles, Clock } from "lucide-react";
import { format } from "date-fns";
import { BrandLogo } from "@/components/BrandLogo";

const ICONS = {
  placed: Circle,
  confirmed: CheckCircle2,
  in_production: Sparkles,
  finishing: Sparkles,
  quality_check: PackageCheck,
  ready_dispatched: Truck,
};

export default function PublicTrackerPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/track/${token}`;
        const { data } = await axios.get(url);
        setState({ loading: false, data, error: null });
      } catch (e) {
        setState({ loading: false, data: null, error: e.response?.data?.detail || "Link expired or invalid" });
      }
    })();
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center text-[#4A5A50]" data-testid="tracker-loading">
        <Loader2 className="animate-spin mr-2" size={18}/> Loading your order…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-[#E4DFCF] p-8" data-testid="tracker-error">
          <div className="font-serif text-2xl">Link not found</div>
          <div className="text-[#4A5A50] mt-2 text-sm">{state.error}</div>
        </div>
      </div>
    );
  }

  const d = state.data;
  const currentIdx = d.milestones.findIndex(m => m.state === "current");
  const doneCount = d.milestones.filter(m => m.state === "done").length;
  const pct = Math.round(((doneCount + (currentIdx >= 0 ? 0.5 : 0)) / d.milestones.length) * 100);

  return (
    <div className="min-h-screen bg-[#FAF7EF] bg-grain" data-testid="public-tracker-page">
      {/* Header */}
      <header className="bg-[#083F22] text-white">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <BrandLogo size={40} showWordmark variant="dark" testId="tracker-brand"/>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A24B]">Crafted at</div>
            <div className="text-sm font-medium">{d.company_name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Order card */}
        <div className="bg-white border border-[#E4DFCF] rounded-2xl p-6 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Order</div>
          <div className="font-serif text-3xl mt-1" data-testid="tracker-reference">{d.reference}</div>
          <div className="mt-1 text-sm text-[#4A5A50]">
            {d.items_count} item{d.items_count === 1 ? "" : "s"} · being handcrafted for you
          </div>

          <div className="mt-6 h-2 bg-[#F1EDDF] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#0F5E39] to-[#C9A24B] transition-all" style={{ width: `${pct}%` }} data-testid="tracker-progress"/>
          </div>

          {d.eta && (
            <div className="mt-4 flex items-center gap-2 text-sm text-[#4A5A50]">
              <Clock size={14} className="text-[#C9A24B]"/>
              <span>Expected on <span className="text-[#083F22] font-medium">{format(new Date(d.eta), "dd MMM yyyy")}</span></span>
            </div>
          )}
        </div>

        {/* Milestones */}
        <div className="bg-white border border-[#E4DFCF] rounded-2xl p-6" data-testid="tracker-milestones">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50] mb-4">Your piece is here in the journey</div>
          <div className="relative pl-8">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[#E4DFCF]"/>
            {d.milestones.map((m) => {
              const Icon = ICONS[m.key] || Circle;
              const isDone = m.state === "done";
              const isCurrent = m.state === "current";
              return (
                <div key={m.key} className="relative pb-6 last:pb-0" data-testid={`milestone-${m.key}`}>
                  <div className={`absolute -left-8 top-0 w-6 h-6 rounded-full flex items-center justify-center ${
                    isDone ? "bg-[#0F5E39] text-white" :
                    isCurrent ? "bg-[#C9A24B] text-white ring-4 ring-[#C9A24B]/25 animate-pulse" :
                    "bg-white border border-[#E4DFCF] text-[#4A5A50]"
                  }`}>
                    <Icon size={12} strokeWidth={2}/>
                  </div>
                  <div>
                    <div className={`font-medium ${isCurrent ? "text-[#083F22] font-serif text-lg" : "text-[#0F1E15]"}`}>{m.label}</div>
                    <div className="text-xs text-[#4A5A50] mt-0.5">{m.subtitle}</div>
                    {isCurrent && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#6E5416] px-2 py-1 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A24B] animate-pulse"/> In progress now
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center text-xs text-[#4A5A50] py-4">
          You'll receive a notification when the next milestone is reached.
        </div>
      </main>
    </div>
  );
}
